import dayjs from "dayjs";
import { Currency } from "@farmact/model/src/model/Currency";
import { Locale } from "@farmact/model/src/model/Locale";
import { LocaleFormatterGenerator } from "./customHooks/useLocaleFormatter";

// Standalone Formatters usable outside of React

export function getNumberFormatter(locale: Locale, fractionDigits = 2) {
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

export function getCurrencyFormatter(locale: Locale, currency: string, fractionDigits?: number) {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: fractionDigits,
    });
}

function getPercentFormatter(locale: Locale, fractionDigits = 2) {
    return new Intl.NumberFormat(locale, {
        style: "percent",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

export function getAreaFormatter(locale: Locale, fractionDigits = 2) {
    return {
        format: (areaSquareMeters: number) => {
            const isSmallArea = areaSquareMeters < 1000;

            const formatter = getNumberFormatter(locale, fractionDigits);
            return isSmallArea
                ? `${formatter.format(areaSquareMeters)} m²`
                : `${formatter.format(areaSquareMeters / 10000)} ha`;
        },
    };
}

export function getDistanceFormatter(locale: Locale, fractionDigits = 2) {
    return {
        format: (distanceMeters: number) => {
            const isSmallDistance = distanceMeters < 1000;

            const formatter = getNumberFormatter(locale, fractionDigits);
            return isSmallDistance
                ? `${formatter.format(distanceMeters)} m`
                : `${formatter.format(distanceMeters / 1000)} km`;
        },
    };
}

export function getDateFormatter(locale: Locale) {
    return {
        format: (date: Date | dayjs.Dayjs) => {
            switch (locale) {
                case Locale.DE_DE:
                case Locale.DE_CH:
                    return dayjs(date).format("DD.MM.YYYY");
                default:
                    return dayjs(date).format("DD.MM.YYYY");
            }
        },
    };
}

export function getDateTimeFormatter(locale: Locale) {
    return {
        format: (date: Date | dayjs.Dayjs) => {
            switch (locale) {
                case Locale.DE_DE:
                case Locale.DE_CH:
                    return dayjs(date).format("DD.MM.YYYY HH:mm");
                default:
                    return dayjs(date).format("DD.MM.YYYY HH:mm");
            }
        },
    };
}

export function getCustomDateFormatter(locale: Locale, options?: Intl.DateTimeFormatOptions) {
    return new Intl.DateTimeFormat(locale, options);
}

export function getSingularPluralTextFormatter(singular: string, plural: string) {
    return (count: number) => {
        return count === 1 ? singular : plural;
    };
}

// `useLocaleFormatter` compatible formatters

/**
 * @param currency defaults to EUR
 * @param fractionDigits defaults to 2
 */
export const currencyFormatterGenerator = (
    currency = Currency.EUR,
    fractionDigits = 2
): LocaleFormatterGenerator<number> => {
    return locale => {
        return getCurrencyFormatter(locale, currency, fractionDigits);
    };
};

export const dateFormatterGenerator: LocaleFormatterGenerator<Date | dayjs.Dayjs> = locale => getDateFormatter(locale);

export const areaFormatterGenerator = (fractionDigits = 2): LocaleFormatterGenerator<number> => {
    return locale => getAreaFormatter(locale, fractionDigits);
};

export const distanceFormatterGenerator = (fractionDigits = 2): LocaleFormatterGenerator<number> => {
    return locale => getDistanceFormatter(locale, fractionDigits);
};

export const numberFormatterGenerator = (fractionDigits = 2): LocaleFormatterGenerator<number> => {
    return locale => getNumberFormatter(locale, fractionDigits);
};

export const percentFormatterGenerator = (fractionDigits = 2): LocaleFormatterGenerator<number> => {
    return locale => getPercentFormatter(locale, fractionDigits);
};

interface FormatListOptions {
    /**
     * @default 3
     */
    cap: number;
    /**
     * @default "-"
     */
    empty: string;
    /**
     * Used as postfix if there is exactly 1 remaining item
     * @default "weitere"
     */
    postfixSingular: string;
    /**
     * Used as postfix if there are more than 1 remaining items
     * @default "weitere"
     */
    postfixPlural: string;
}
const defaultFormatListOptions: FormatListOptions = {
    cap: 3,
    empty: "-",
    postfixSingular: "weitere",
    postfixPlural: "weitere",
};
export function formatList(list: string[], options: Partial<FormatListOptions> = {}): string {
    const mergedOptions = {
        ...defaultFormatListOptions,
        ...options,
    };

    if (list.length > mergedOptions.cap) {
        const part = list.slice(0, mergedOptions.cap);
        const rest = list.slice(mergedOptions.cap);

        const spFormatter = getSingularPluralTextFormatter(mergedOptions.postfixSingular, mergedOptions.postfixPlural);

        return `${part.join(", ")} und ${rest.length} ${spFormatter(rest.length)}`;
    }

    return list.join(", ");
}
